import {
  ReviewCountBadge,
  H3,
  Paragraph,
  SynonymAccordionTitle,
  SynonymAccordionText,
  SynonymAccordionHeader,
  AccordionLeadSlot,
  ACCORDION_BODY_INDENT,
  SynonymTermAccordionTitle,
  TermChips,
  TermChip,
} from './styled';
import { useDispatch, useSelector } from 'react-redux';
import { getNLPModalState } from 'selectors/searchNLP';
import {
  togglePluralsEnabled,
  togglePluralTermEnabled,
  togglePluralVariant,
} from 'actions/searchNLP';
import Accordion from 'components/common/Accordion';
import { Switch, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { selectedDistinctLowers } from 'reducers/searchNLP/countDistinctSelections';
import { isExcludedTerm } from 'reducers/searchNLP/termSides';
import { chipStateTooltip } from './chipTooltip';

/**
 * True when at least one available suggestion is not in the selected set — i.e.
 * the panel hides an off-by-default variant the user might otherwise miss.
 *
 * @param {string[]} available
 * @param {string[]} selected
 * @returns {boolean}
 */
const hasUnselectedSuggestion = (available, selected) =>
  available.some(item => !selected.includes(item));

/**
 * Tooltip only for variants listed under a non-base source (synonym-derived).
 *
 * @param {string} baseTerm - accordion row key (e.g. extracted term)
 * @param {string} variant - chip label
 * @param {Record<string, string[]>|undefined} sources - termPluralSources[baseTerm]
 * @returns {string|null}
 */
function getPluralVariantTooltip(baseTerm, variant, sources) {
  if (!sources || typeof sources !== 'object') return null;
  const vLower = variant.toLowerCase();
  const baseLower = baseTerm.toLowerCase();

  for (const [sourceKey, vars] of Object.entries(sources)) {
    if (sourceKey.toLowerCase() === baseLower) continue;
    if (!Array.isArray(vars)) continue;
    if (vars.some(x => x.toLowerCase() === vLower)) {
      return `From related term: ${sourceKey}`;
    }
  }

  return null;
}

/**
 * Combine the side/conflict tooltip with the provenance tooltip. When both apply,
 * they stack on separate lines; otherwise whichever exists (or null).
 *
 * @param {string|null} stateTip
 * @param {string|null} provenance
 * @returns {import('react').ReactNode|string|null}
 */
function combinePluralTooltip(stateTip, provenance) {
  if (stateTip && provenance) {
    return (
      <>
        {stateTip}
        <br />
        {provenance}
      </>
    );
  }
  return stateTip || provenance;
}

/**
 * Plural / variant review UI for NLP stage 2 (parallel to {@link NLPSynonymAccordion}).
 */
export function NLPPluralAccordion({ conflictLabels }) {
  const dispatch = useDispatch();
  const {
    aiSearchInterpretation,
    pluralsEnabled,
    pluralTermEnabled,
    selectedPluralVariants,
    termSideMap,
  } = useSelector(getNLPModalState);

  const termPlurals = aiSearchInterpretation?.termPlurals;
  const termPluralSources = aiSearchInterpretation?.termPluralSources;

  // Distinct count: collapse duplicates that the chip-link creates when the same
  // plural is shown under multiple OR-sibling sub-panels, and exclude selections
  // under terms whose row Switch is off (they don't go into the final query).
  const totalSelectedPluralVariants = useMemo(
    () =>
      selectedDistinctLowers(selectedPluralVariants, pluralTermEnabled).size,
    [selectedPluralVariants, pluralTermEnabled]
  );

  if (!termPlurals) return null;
  const termEntries = Object.entries(termPlurals);
  if (termEntries.length <= 0) return null;
  const hasSingleTerm = termEntries.length === 1;

  return (
    <Accordion
      // Always open — with opt-in defaults the user must be able to see and pick
      // suggestions without an extra click, whatever is currently selected.
      isCollapsedByDefault={false}
      bodyIndent={ACCORDION_BODY_INDENT}
      title={
        <SynonymAccordionTitle>
          <AccordionLeadSlot>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={e => e.stopPropagation()}>
              <Switch
                data-testid='nlp-plurals-master-switch'
                checked={pluralsEnabled}
                onChange={() => dispatch(togglePluralsEnabled())}
                size='small'
              />
            </span>
          </AccordionLeadSlot>
          <SynonymAccordionText>
            <SynonymAccordionHeader>
              <H3>Include plural & singular forms</H3>
              <ReviewCountBadge>{totalSelectedPluralVariants}</ReviewCountBadge>
            </SynonymAccordionHeader>
            <Paragraph>
              Edit plural and singular forms created from your search
            </Paragraph>
          </SynonymAccordionText>
        </SynonymAccordionTitle>
      }
      testId='nlp-search-review-plurals'
    >
      {termEntries.map(([term, variants]) => (
        <NLPTermPluralContent
          key={term}
          term={term}
          variants={variants}
          pluralSources={termPluralSources?.[term]}
          excluded={isExcludedTerm(termSideMap, term)}
          conflictLabels={conflictLabels}
          selectedVariants={selectedPluralVariants[term] || []}
          termEnabled={!!pluralTermEnabled[term]}
          isSingleTerm={hasSingleTerm}
          onToggleTerm={() => dispatch(togglePluralTermEnabled(term))}
          onToggleVariant={variant =>
            dispatch(togglePluralVariant(term, variant))
          }
        />
      ))}
    </Accordion>
  );
}

/**
 * @param props
 * @param {string} props.term
 * @param {string[]} props.variants
 * @param {Record<string, string[]>|undefined} props.pluralSources
 * @param {boolean} props.excluded - term sits on the exclusion side (red chips)
 * @param {Set<string>} [props.conflictLabels] - lowercased labels selected on both sides (amber)
 * @param {string[]} props.selectedVariants
 * @param {boolean} props.termEnabled
 * @param {() => void} props.onToggleTerm
 * @param {(variant: string) => void} props.onToggleVariant
 */
function NLPTermPluralContent(props) {
  const isConflicted = (variant, isSelected) =>
    isSelected && !!props.conflictLabels?.has(variant.toLowerCase());

  if (props.isSingleTerm) {
    return (
      <>
        <SynonymTermAccordionTitle>
          {props.term}
          <ReviewCountBadge>{props.selectedVariants.length}</ReviewCountBadge>
        </SynonymTermAccordionTitle>
        <TermChips>
          {props.variants.map(variant => {
            const isSelected = props.selectedVariants.includes(variant);
            const conflicted = isConflicted(variant, isSelected);
            const tooltip = combinePluralTooltip(
              chipStateTooltip({
                label: variant,
                excluded: props.excluded,
                selected: isSelected,
                conflicted,
              }),
              getPluralVariantTooltip(props.term, variant, props.pluralSources)
            );
            const chip = (
              <TermChip
                excluded={props.excluded}
                conflict={conflicted}
                data-testid={`nlp-plural-chip-${props.term}-${variant}`}
                selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => props.onToggleVariant(variant)}
              >
                {variant}
                {isSelected && <span aria-hidden='true'>×</span>}
              </TermChip>
            );
            return tooltip ? (
              <Tooltip key={variant} title={tooltip}>
                {chip}
              </Tooltip>
            ) : (
              <React.Fragment key={variant}>{chip}</React.Fragment>
            );
          })}
        </TermChips>
      </>
    );
  }

  return (
    <Accordion
      isCollapsedByDefault={
        !hasUnselectedSuggestion(props.variants, props.selectedVariants)
      }
      title={
        <SynonymTermAccordionTitle>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <span onClick={e => e.stopPropagation()}>
            <Switch
              data-testid={`nlp-plural-switch-${props.term}`}
              checked={props.termEnabled}
              onChange={props.onToggleTerm}
              size='small'
            />
          </span>
          {props.term}
          <ReviewCountBadge>{props.selectedVariants.length}</ReviewCountBadge>
        </SynonymTermAccordionTitle>
      }
      testId={`nlp-plural-term-${props.term}`}
    >
      <TermChips>
        {props.variants.map(variant => {
          const isSelected = props.selectedVariants.includes(variant);
          const conflicted = isConflicted(variant, isSelected);
          const tooltip = combinePluralTooltip(
            chipStateTooltip({
              label: variant,
              excluded: props.excluded,
              selected: isSelected,
              conflicted,
            }),
            getPluralVariantTooltip(props.term, variant, props.pluralSources)
          );
          const chip = (
            <TermChip
              excluded={props.excluded}
              conflict={conflicted}
              data-testid={`nlp-plural-chip-${props.term}-${variant}`}
              selected={isSelected}
              aria-pressed={isSelected}
              onClick={() => props.onToggleVariant(variant)}
            >
              {variant}
              {isSelected && <span aria-hidden='true'>×</span>}
            </TermChip>
          );
          return tooltip ? (
            <Tooltip key={variant} title={tooltip}>
              {chip}
            </Tooltip>
          ) : (
            <React.Fragment key={variant}>{chip}</React.Fragment>
          );
        })}
      </TermChips>
    </Accordion>
  );
}
